// SEQ-180 phase 2, tasks P15 and P18: the four column widget grid, with
// reorder and resize in edit mode.
//
// Reorder uses @dnd-kit with a KeyboardSensor and sortableKeyboardCoordinates,
// the pattern already in this repo (EditCategoriesModal.tsx), so a widget can
// be moved with the keyboard alone: tab to the handle, space to pick up,
// arrows to move, space to drop. Resize is a select, not a drag corner, for
// the same reason (plan section 7.4): a drag corner may be added later as an
// alternative, never as the only path.
//
// Order lives in the `position` column and size in `layout` (D18), so the
// two controls write different things and can never disagree.
import type { ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Plus, Table2, Trash2 } from "lucide-react";
import type { PnlWidgetConfig, PnlWidgetLayout } from "../../lib/pnl/pnlWidgetSchema";
import { WIDGET_SIZE_OPTIONS, widgetSizeValue } from "../../features/pnlAnalytics/widgetSizes";
import {
  gridContainerVariants,
  PNL_EXIT_TWEEN,
  PNL_LAYOUT_SPRING,
  PNL_TACTILE_TWEEN,
  widgetItemVariants,
} from "../../features/pnlAnalytics/pnlMotion";
import "./PnlDashboardGrid.css";

export interface PnlGridWidget {
  /** The row id for a saved widget, or the config's own widgetId on the code-defined default dashboard. */
  id: string;
  config: PnlWidgetConfig;
  layout: PnlWidgetLayout;
}

interface WidgetFrameProps {
  widget: PnlGridWidget;
  editing: boolean;
  onResize: (id: string, layout: PnlWidgetLayout) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  children: ReactNode;
}

function WidgetFrame({ widget, editing, onResize, onEdit, onDelete, children }: WidgetFrameProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isSorting } = useSortable({
    id: widget.id,
    disabled: !editing,
  });
  const reduce = useReducedMotion() ?? false;

  const sizeValue = widgetSizeValue(widget.layout);
  const sizeLabelId = `pnl-widget-size-${widget.id}`;

  // TWO ANIMATION SYSTEMS, ONE ELEMENT, SO THE DIVISION IS EXPLICIT.
  //
  // dnd-kit owns the live drag: it writes the pointer offset into `transform`
  // and shifts the other cards with its own `transition`. Framer owns the
  // RESIZE, which dnd-kit knows nothing about and which used to make a card
  // and all its neighbours jump between grid spans.
  //
  // THE GATE IS `isSorting`, NOT `isDragging`. Code review, HIGH: this read
  // `!isDragging`, which is true only of the card under the pointer. Every
  // OTHER card gets a non-null `transform` from rectSortingStrategy the moment
  // a drag starts while reporting `isDragging === false`, so in edit mode each
  // displaced neighbour had framer's projection AND dnd-kit's transform
  // writing the same property for the whole drag, which is exactly what this
  // comment claimed could not happen. Worse, those same neighbours were the
  // ones losing dnd-kit's `transition` string on the line below, so the cards
  // that most need to slide were the cards told not to. `isSorting` is
  // `Boolean(active)`: true for every card while any drag is live, which is
  // the condition this paragraph always meant.
  //
  // It is `layout="position"` and not `layout` because the card holds an SVG
  // chart: animating its size would scale-distort the chart for the length of
  // the animation, while animating position alone slides the neighbours and
  // lets the chart redraw at its new size in one step.
  const layoutAnimated = editing && !isSorting && !reduce;

  return (
    <motion.section
      ref={setNodeRef}
      className="pnl-widget"
      aria-label={widget.config.name}
      data-w={widget.layout.w}
      data-h={widget.layout.h}
      data-editing={editing ? "true" : "false"}
      data-dragging={isDragging ? "true" : "false"}
      variants={widgetItemVariants(reduce)}
      exit={{ opacity: 0, scale: reduce ? 1 : 0.97, transition: PNL_EXIT_TWEEN }}
      layout={layoutAnimated ? "position" : false}
      transition={PNL_LAYOUT_SPRING}
      style={{ transform: CSS.Transform.toString(transform), transition: layoutAnimated ? undefined : transition }}
    >
      {editing && (
        <div className="pnl-widget-toolbar">
          <button
            type="button"
            className="pnl-widget-handle"
            aria-label={`Reorder ${widget.config.name}`}
            {...attributes}
            {...listeners}
          >
            <GripVertical size={14} aria-hidden="true" />
          </button>
          <span className="pnl-widget-toolbar-name">{widget.config.name}</span>
          <div className="pnl-widget-toolbar-actions">
            <label htmlFor={sizeLabelId} className="pnl-visually-hidden">
              Size of {widget.config.name}
            </label>
            <select
              id={sizeLabelId}
              className="form-input"
              style={{ padding: "3px 6px", fontSize: 12, width: "auto" }}
              value={sizeValue}
              onChange={(e) => {
                const option = WIDGET_SIZE_OPTIONS.find((o) => o.value === e.target.value);
                if (option) onResize(widget.id, option.layout);
              }}
            >
              {WIDGET_SIZE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              {!WIDGET_SIZE_OPTIONS.some((o) => o.value === sizeValue) && (
                <option value={sizeValue}>{`${widget.layout.w} by ${widget.layout.h}`}</option>
              )}
            </select>
            <button
              type="button"
              className="pnl-widget-icon-button"
              aria-label={`Edit ${widget.config.name}`}
              onClick={() => onEdit(widget.id)}
            >
              <Pencil size={13} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="pnl-widget-icon-button"
              data-destructive="true"
              aria-label={`Delete ${widget.config.name}`}
              onClick={() => onDelete(widget.id)}
            >
              <Trash2 size={13} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
      {children}
    </motion.section>
  );
}

export interface PnlDashboardGridProps {
  widgets: PnlGridWidget[];
  editing: boolean;
  onReorder: (orderedIds: string[]) => void;
  onResize: (id: string, layout: PnlWidgetLayout) => void;
  onEditWidget: (id: string) => void;
  onDeleteWidget: (id: string) => void;
  onAddWidget: () => void;
  /** SEQ-199. Adds the ready-made Profit Pareto grid in one step. */
  onAddProfitPareto: () => void;
  /** True while the one-step add is saving, so the tile cannot be pressed twice. */
  addingProfitPareto?: boolean;
  renderWidget: (widget: PnlGridWidget) => ReactNode;
}

export function PnlDashboardGrid({
  widgets,
  editing,
  onReorder,
  onResize,
  onEditWidget,
  onDeleteWidget,
  onAddWidget,
  onAddProfitPareto,
  addingProfitPareto = false,
  renderWidget,
}: PnlDashboardGridProps) {
  const reduce = useReducedMotion() ?? false;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = widgets.map((w) => w.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onReorder(arrayMove(ids, from, to));
  };

  // The entrance runs once, when the grid mounts. A data refresh re-renders
  // these same nodes rather than remounting them, so widgets do not re-deal
  // themselves every time the filter bar changes, which would be motion for
  // its own sake on a page people leave open all day.
  //
  // mode="popLayout" is what makes a deleted widget leave properly: the card
  // is taken out of the grid's flow the moment it starts fading, so its
  // neighbours close the gap while it goes rather than after.
  const grid = (
    <motion.div
      className="pnl-dashboard-grid"
      variants={gridContainerVariants(reduce, widgets.length)}
      initial="hidden"
      animate="visible"
    >
      {/* No `initial={false}`: that switch suppresses the first render's
          animation for every child, which silently cancelled the entrance the
          container's variants are there to drive. Exit still works without
          it. */}
      <AnimatePresence mode="popLayout">
        {widgets.map((widget) => (
          <WidgetFrame
            key={widget.id}
            widget={widget}
            editing={editing}
            onResize={onResize}
            onEdit={onEditWidget}
            onDelete={onDeleteWidget}
          >
            {renderWidget(widget)}
          </WidgetFrame>
        ))}
      </AnimatePresence>
      {editing && (
        <motion.section
          className="pnl-widget"
          data-w="2"
          data-h="1"
          aria-label="Add a widget"
          variants={widgetItemVariants(reduce)}
          initial="hidden"
          animate="visible"
        >
          <motion.button
            type="button"
            className="pnl-add-widget-tile"
            onClick={onAddWidget}
            whileHover={reduce ? undefined : { scale: 1.01 }}
            whileTap={reduce ? undefined : { scale: 0.985 }}
            transition={PNL_TACTILE_TWEEN}
          >
            <Plus size={20} aria-hidden="true" />
            Add widget
          </motion.button>
        </motion.section>
      )}
      {editing && (
        <motion.section
          className="pnl-widget"
          data-w="2"
          data-h="1"
          aria-label="Add the Profit Pareto grid"
          variants={widgetItemVariants(reduce)}
          initial="hidden"
          animate="visible"
        >
          <motion.button
            type="button"
            className="pnl-add-widget-tile"
            onClick={onAddProfitPareto}
            disabled={addingProfitPareto}
            aria-busy={addingProfitPareto}
            whileHover={reduce ? undefined : { scale: 1.01 }}
            whileTap={reduce ? undefined : { scale: 0.985 }}
            transition={PNL_TACTILE_TWEEN}
          >
            <Table2 size={20} aria-hidden="true" />
            {addingProfitPareto ? "Adding..." : "Add Profit Pareto"}
          </motion.button>
        </motion.section>
      )}
    </motion.div>
  );

  // React review, HIGH: the root component TYPE must not change when
  // `editing` flips. This used to return a bare div in view mode and the same
  // div wrapped in DndContext in edit mode, so React tore the whole tree down
  // and rebuilt it on every toggle. That threw away the local state the phase
  // 1 widgets deliberately keep: the performance table's text filter, sort and
  // column choices, the trend chart's selected project, both scatters' axis
  // choice. Filtering the table and then opening Edit to nudge one card lost
  // the filter, which is the opposite of what a builder should do.
  //
  // The context is now always mounted and dragging is turned off per widget
  // through `useSortable({ disabled: !editing })`, which was already in place.
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={widgets.map((w) => w.id)} strategy={rectSortingStrategy}>
        {grid}
      </SortableContext>
    </DndContext>
  );
}
